import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import API, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BetaBadge } from "@/components/BetaBadge";
import { KalibLogo } from "@/components/KalibLogo";
import { ArrowLeft, Check } from "lucide-react";

function formatLaunchDate(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00Z");
  if (isNaN(d.getTime())) return iso;
  const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  return `${d.getUTCDate()} ${months[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

export default function WaitlistPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [pricing, setPricing] = useState("monthly");
  const [config, setConfig] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user?.email) setEmail(user.email);
  }, [user]);

  useEffect(() => {
    API.get("/config/trial-window").then(({ data }) => setConfig(data)).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim() || !email.includes("@")) {
      setError("Please enter a valid email address.");
      return;
    }
    setSubmitting(true);
    try {
      await API.post("/waitlist", {
        email: email.trim().toLowerCase(),
        pricing_preference: pricing,
      });
      setSubmitted(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const launchLabel = formatLaunchDate(config?.trial_end_date);
  const monthlyPrice = config?.monthly_price_gbp ?? 20;
  const annualPrice = config?.annual_price_gbp ?? 300;

  return (
    <div className="min-h-screen bg-zinc-100 flex flex-col" data-testid="waitlist-page">
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Link to="/" className="block" data-testid="waitlist-logo-link" aria-label="Kalib AI home">
              <KalibLogo variant="light" height={28} testId="waitlist-header-logo" />
            </Link>
            <BetaBadge />
          </div>
          {user ? (
            <Link to="/intelligence" className="inline-flex items-center gap-1.5 text-sm text-zinc-900 hover:text-zinc-950 transition-colors" data-testid="waitlist-back-link">
              <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.75} />
              Back to Reveal
            </Link>
          ) : (
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-zinc-900 hover:text-zinc-950 transition-colors">
              <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.75} />
              Back
            </Link>
          )}
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="max-w-lg w-full">
          {submitted ? (
            <div className="bg-white border border-zinc-200 p-8 text-center" data-testid="waitlist-success">
              <div className="w-10 h-10 bg-emerald-50 border border-emerald-200 mx-auto mb-4 flex items-center justify-center">
                <Check className="w-5 h-5 text-emerald-700" strokeWidth={2} />
              </div>
              <h2 className="text-xl font-medium tracking-tight text-zinc-950 font-heading mb-2">You&apos;re on the list.</h2>
              <p className="text-sm text-zinc-900 mb-6">
                We&apos;ll email {email} the moment payment opens{launchLabel ? ` on ${launchLabel}` : ""}. Trial pricing locked in.
              </p>
              {user ? (
                <Button
                  onClick={() => navigate("/intelligence")}
                  className="bg-amber-700 text-white hover:bg-amber-800 rounded-none text-sm font-medium px-6 py-2 h-auto"
                  data-testid="waitlist-back-to-reveal-btn"
                >
                  Back to your Reveal
                </Button>
              ) : (
                <Link to="/" className="text-sm text-amber-700 hover:text-amber-800 font-medium">Back to home</Link>
              )}
            </div>
          ) : (
            <>
              <p className="uppercase mb-3" style={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.18em", color: "#B45309" }}>
                Reserve your seat
              </p>
              <h1 className="text-3xl sm:text-4xl font-medium tracking-tight text-zinc-950 font-heading mb-3" data-testid="waitlist-title">
                Kalib AI paid subscriptions launch{launchLabel ? ` ${launchLabel}` : " soon"}.
              </h1>
              <p className="text-[15px] text-zinc-700 leading-relaxed mb-8">
                Reserve your seat at trial pricing — monthly or annual — and we&apos;ll email when payment opens. No card required now.
              </p>

              <form onSubmit={handleSubmit} className="bg-white border border-zinc-200 p-6 space-y-6" data-testid="waitlist-form">
                {/* Email */}
                <div>
                  <label className="text-xs font-medium uppercase tracking-widest text-zinc-900 block mb-1.5">Your email</label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="rounded-none border-zinc-200 focus:ring-amber-700 focus:border-amber-700"
                    data-testid="waitlist-email-input"
                    disabled={!!user}
                  />
                  {user && (
                    <p className="text-[11px] text-zinc-900 mt-1">Using your account email.</p>
                  )}
                </div>

                {/* Pricing preference */}
                <div>
                  <label className="text-xs font-medium uppercase tracking-widest text-zinc-900 block mb-1.5">Pricing preference</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setPricing("monthly")}
                      aria-pressed={pricing === "monthly"}
                      className={`text-left p-4 border transition-all ${
                        pricing === "monthly"
                          ? "border-amber-700 bg-amber-50 ring-1 ring-amber-700"
                          : "border-zinc-200 bg-white hover:border-zinc-300"
                      }`}
                      data-testid="waitlist-pricing-monthly"
                    >
                      <span className="block text-sm font-medium text-zinc-950">Monthly</span>
                      <span className="block text-[13px] text-zinc-900 mt-0.5">£{monthlyPrice} / month</span>
                      <span className={`block mt-1 text-[11px] ${pricing === "monthly" ? "text-amber-700" : "text-zinc-900"}`}>
                        Flexible, cancel anytime
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPricing("annual")}
                      aria-pressed={pricing === "annual"}
                      className={`text-left p-4 border transition-all ${
                        pricing === "annual"
                          ? "border-amber-700 bg-amber-50 ring-1 ring-amber-700"
                          : "border-zinc-200 bg-white hover:border-zinc-300"
                      }`}
                      data-testid="waitlist-pricing-annual"
                    >
                      <span className="block text-sm font-medium text-zinc-950">
                        Annual
                        <span className="ml-2 inline-flex items-center px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-widest bg-emerald-50 text-emerald-700 border border-emerald-200 leading-none">
                          Save £{(monthlyPrice * 12) - annualPrice}
                        </span>
                      </span>
                      <span className="block text-[13px] text-zinc-900 mt-0.5">£{annualPrice} / year</span>
                      <span className={`block mt-1 text-[11px] ${pricing === "annual" ? "text-amber-700" : "text-zinc-900"}`}>
                        Best rate, locked in
                      </span>
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2" data-testid="waitlist-error">{error}</div>
                )}

                <Button
                  type="submit"
                  disabled={submitting || !email.trim()}
                  className="w-full bg-amber-700 text-white hover:bg-amber-800 rounded-none text-sm font-medium px-6 py-3 h-auto disabled:opacity-40"
                  data-testid="waitlist-submit-btn"
                >
                  {submitting ? "Reserving..." : "Reserve my seat"}
                </Button>

                <p className="text-[11px] text-zinc-900 text-center">
                  No card taken now. We&apos;ll email you when payment opens.
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
